import React from "react";
import classnames from "classnames";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Card, Col, FormGroup, Input, Label, Row } from "reactstrap";
import FileIcon from "Components/Common/FileIcon";
import { OTHER_SPECIFY_OPTION, PseaCoreStandard } from "./pseaCoreStandards";
import PSEAMinimumRequirements from "./PSEAMinimumRequirements";
import { PseaAnswer, pseaAnswer } from "./pseaScoring";
import { supportingDocumentError } from "./pseaValidation";

function formatBytes(bytes: any, decimals = 2) {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

const PSEACoreStandardItem = ({
  standard,
  index,
  validation,
}: {
  standard: PseaCoreStandard;
  index: number;
  validation: any;
}) => {
  const value = validation.values.coreStandards?.[index] || {
    standardNumber: standard.number,
    meetsStandard: null,
    notApplicable: false,
    evidence: [],
    otherSpecify: "",
    comment: "",
    selectedFiles: [],
  };

  const setValue = (patch: any) => {
    const next = [...(validation.values.coreStandards || [])];
    next[index] = { ...value, ...patch };
    validation.setFieldValue("coreStandards", next);
  };

  const toggleEvidence = (option: string) => {
    const current: string[] = value.evidence || [];
    const exists = current.includes(option);
    const evidence = exists
      ? current.filter((o) => o !== option)
      : [...current, option];
    const patch: any = { evidence };
    if (option === OTHER_SPECIFY_OPTION && exists) {
      patch.otherSpecify = "";
    }
    setValue(patch);
  };

  const handleAcceptedFiles = (files: any[]) => {
    const withMeta = files.map((file: any) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setValue({ selectedFiles: [...(value.selectedFiles || []), ...withMeta] });
  };

  const handleRemoveFile = (fileIndex: number) => {
    const files = [...(value.selectedFiles || [])];
    files.splice(fileIndex, 1);
    setValue({ selectedFiles: files });
  };

  const showOtherSpecify = (value.evidence || []).includes(
    OTHER_SPECIFY_OPTION
  );

  // Standard 8 is about corrective action for past SEA allegations, so "Not
  // applicable" reads as "No Allegations" there specifically.
  const notApplicableLabel =
    standard.number === 8 ? "No Allegations" : "Not applicable";

  // Surfaced on the first submit attempt rather than on touch: the dropzone is
  // not a plain input, so it never registers a blur to mark itself touched.
  const fileError = supportingDocumentError(validation.errors, index);
  const showFileError = Boolean(fileError) && validation.submitCount > 0;

  // Evidence and supporting documents are only relevant when the standard is
  // met; "No" and "Not applicable" leave just the comment for an explanation.
  const answer = pseaAnswer(value);
  const showEvidence = answer === "yes";

  const setAnswer = (next: Exclude<PseaAnswer, null>) => {
    if (answer === next) return;
    // `notApplicable` rides alongside `meetsStandard` rather than inside it,
    // so both are always written together to keep the pair consistent.
    const patch: any = {
      meetsStandard: next === "yes" ? true : next === "no" ? false : null,
      notApplicable: next === "na",
    };
    // Clear evidence captured before switching away from "Yes" so a saved
    // record can never claim evidence for a standard it does not meet.
    if (next !== "yes") {
      patch.evidence = [];
      patch.otherSpecify = "";
      patch.selectedFiles = [];
    }
    setValue(patch);
  };

  return (
    <div>
      {/* pre-wrap so multi-line descriptions keep their list formatting. */}
      {standard.description || standard.minimumRequirements ? (
        <p className="text-muted fs-13 mb-3" style={{ whiteSpace: "pre-wrap" }}>
          {standard.description}
          {standard.minimumRequirements?.length ? (
            <PSEAMinimumRequirements
              standardNumber={standard.number}
              requirements={standard.minimumRequirements}
            />
          ) : null}
        </p>
      ) : null}

      <FormGroup className="mb-3">
        <Label className="form-label fw-semibold mb-2 d-block">
          Does the organization meet this standard?
        </Label>
        <div className="d-flex gap-4 flex-wrap">
          <div className="form-check">
            <Input
              type="radio"
              className="form-check-input"
              name={`std-${standard.number}-meets`}
              id={`std-${standard.number}-meets-yes`}
              checked={answer === "yes"}
              onChange={() => setAnswer("yes")}
            />
            <Label
              className="form-check-label"
              htmlFor={`std-${standard.number}-meets-yes`}
            >
              Yes
            </Label>
          </div>
          <div className="form-check">
            <Input
              type="radio"
              className="form-check-input"
              name={`std-${standard.number}-meets`}
              id={`std-${standard.number}-meets-no`}
              checked={answer === "no"}
              onChange={() => setAnswer("no")}
            />
            <Label
              className="form-check-label"
              htmlFor={`std-${standard.number}-meets-no`}
            >
              No
            </Label>
          </div>
          {standard.allowNotApplicable ? (
            <div className="form-check">
              <Input
                type="radio"
                className="form-check-input"
                name={`std-${standard.number}-meets`}
                id={`std-${standard.number}-meets-na`}
                checked={answer === "na"}
                onChange={() => setAnswer("na")}
              />
              <Label
                className="form-check-label"
                htmlFor={`std-${standard.number}-meets-na`}
              >
                {notApplicableLabel}
              </Label>
            </div>
          ) : null}
        </div>
        {standard.allowNotApplicable ? (
          <small className="text-muted d-block mt-1">
            "{notApplicableLabel}" scores one point, the same as "Yes".
          </small>
        ) : null}
      </FormGroup>

      {showEvidence ? (
        <React.Fragment>
          <Label className="form-label fw-semibold mb-2">
            Evidence provided
          </Label>
          <Row className="mb-2">
            {standard.options.map((option, i) => (
              <Col md={12} key={i} className="mb-1">
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id={`std-${standard.number}-opt-${i}`}
                    checked={(value.evidence || []).includes(option)}
                    onChange={() => toggleEvidence(option)}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor={`std-${standard.number}-opt-${i}`}
                  >
                    {option}
                  </Label>
                </div>
              </Col>
            ))}
          </Row>

          {showOtherSpecify ? (
            <FormGroup className="mb-3">
              <Label className="form-label">Other (please specify)</Label>
              <Input
                type="text"
                className="form-control"
                placeholder="Please specify other evidence"
                value={value.otherSpecify || ""}
                onChange={(e) => setValue({ otherSpecify: e.target.value })}
              />
            </FormGroup>
          ) : null}

          <FormGroup className="mb-3">
            <Label className="form-label">
              Supporting documents <span className="text-danger">*</span>
            </Label>
            <Dropzone
              multiple
              onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  className={classnames("dropzone dz-clickable", {
                    "border border-danger": showFileError,
                  })}
                  style={{ minHeight: "auto" }}
                >
                  <div
                    className="dz-message needsclick py-4"
                    {...getRootProps()}
                  >
                    <div className="mb-2">
                      <i className="display-6 text-muted ri-upload-cloud-2-fill" />
                    </div>
                    <h5 className="fs-14">
                      Drop files here or click to upload supporting documents.
                    </h5>
                    <input {...getInputProps()} />
                  </div>
                </div>
              )}
            </Dropzone>

            {showFileError ? (
              <div className="text-danger fs-12 mt-1">
                <i className="ri-error-warning-line align-bottom me-1"></i>
                {fileError}
              </div>
            ) : null}

            <div className="list-unstyled mb-0 mt-2">
              {(value.selectedFiles || []).map((f: any, i: number) => (
                <Card
                  className="mt-1 mb-0 shadow-none border"
                  key={i + "-psea-file"}
                >
                  <div className="p-2">
                    <Row className="align-items-center">
                      <Col className="col-auto">
                        <div className="avatar-sm">
                          <div className="avatar-title bg-light rounded fs-24">
                            <FileIcon fileName={f?.name} />
                          </div>
                        </div>
                      </Col>
                      <Col>
                        {f?.id ? (
                          <Link
                            to={`${process.env.REACT_APP_FILES_URL}${f?.url}`}
                            target="_blank"
                            className="text-muted font-weight-bold"
                          >
                            {f?.name}
                          </Link>
                        ) : (
                          <Link to="#" className="text-muted font-weight-bold">
                            {f?.name}
                          </Link>
                        )}
                        <p className="mb-0">
                          <strong>{f?.formattedSize}</strong>
                        </p>
                      </Col>
                      <Col className="col-auto">
                        <button
                          type="button"
                          className="btn btn-sm bg-danger-subtle"
                          onClick={() => handleRemoveFile(i)}
                        >
                          <i className="ri-delete-bin-fill text-danger"></i>
                        </button>
                      </Col>
                    </Row>
                  </div>
                </Card>
              ))}
            </div>
          </FormGroup>
        </React.Fragment>
      ) : null}

      <FormGroup className="mb-1">
        <Label className="form-label">Comment</Label>
        <Input
          type="textarea"
          className="form-control"
          rows={2}
          placeholder={
            answer === "no"
              ? "Explain why this standard is not met"
              : answer === "na"
              ? "Explain why this standard does not apply"
              : "Add any comments for this standard"
          }
          value={value.comment || ""}
          onChange={(e) => setValue({ comment: e.target.value })}
        />
      </FormGroup>
    </div>
  );
};

export default PSEACoreStandardItem;
